/**
 * Parent progress dashboard — read-only view of Eva's activity.
 * Implements spec §3.4.
 *
 * One round-trip to /api/dashboard fetches everything. Layout is a
 * coarse grid of stat tiles + two lists (frequent misses, recent
 * sessions). Visually quieter than Eva's surfaces — this is "adult
 * mode" per spec §3.2.
 */
import { useEffect, useState } from "react";

import { api } from "@/lib/api";
import type { DashboardResponse } from "@/lib/api";
import "./ParentDashboard.css";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; data: DashboardResponse };

export function ParentDashboard() {
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await api.getDashboard();
      if (cancelled) return;
      if (res.status !== 200 || !res.body) {
        setState({ kind: "error", message: "Couldn't load the dashboard." });
        return;
      }
      setState({ kind: "ready", data: res.body });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return <p className="dashboard__msg">Loading…</p>;
  }
  if (state.kind === "error") {
    return <p className="dashboard__msg dashboard__msg--error">{state.message}</p>;
  }

  const d = state.data;

  return (
    <div className="dashboard">
      <section className="dashboard__stats" aria-label="At a glance">
        <Stat label="Days played" value={d.daysPlayed} />
        <Stat label="Current streak" value={d.currentStreak} suffix="days" />
        <Stat label="Longest streak" value={d.longestStreak} suffix="days" />
        <Stat
          label="Average score"
          value={
            d.averageFirstPassScore === null
              ? "—"
              : formatScore(d.averageFirstPassScore)
          }
        />
        <Stat label="Words mastered" value={d.totalWordsMastered} />
        {d.mostRecentTimeSpentSeconds !== null ? (
          <Stat
            label="Time per session"
            value={
              d.averageTimeSpentSeconds === null
                ? "—"
                : formatDuration(d.averageTimeSpentSeconds)
            }
            sub={`most recent ${formatDuration(d.mostRecentTimeSpentSeconds)}`}
          />
        ) : (
          <Stat
            label="Time per session"
            value={
              d.averageTimeSpentSeconds === null
                ? "—"
                : formatDuration(d.averageTimeSpentSeconds)
            }
          />
        )}
      </section>

      <section className="dashboard__panel" aria-label="Words she misses most">
        <h2 className="dashboard__panel-title">Often missed (last 60 days)</h2>
        {d.frequentMisses.length === 0 ? (
          <p className="dashboard__empty">Nothing to flag — nice.</p>
        ) : (
          <ul className="dashboard__miss-list">
            {d.frequentMisses.map((m) => (
              <li key={m.spelling} className="dashboard__miss-row">
                <span className="dashboard__miss-word">{m.spelling}</span>
                <span className="dashboard__miss-count">
                  {m.missCount}{" "}
                  <span className="dashboard__miss-count-label">
                    {m.missCount === 1 ? "miss" : "misses"}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="dashboard__panel" aria-label="Recent sessions">
        <h2 className="dashboard__panel-title">Recent sessions</h2>
        {d.recentSessions.length === 0 ? (
          <p className="dashboard__empty">She hasn&rsquo;t played yet.</p>
        ) : (
          <ul className="dashboard__session-list">
            {d.recentSessions.map((s) => (
              <li key={s.id} className="dashboard__session-row">
                <div className="dashboard__session-meta">
                  <span className="dashboard__session-date">
                    {formatDate(s.startedAt)}
                  </span>
                  {!s.countsTowardProgress && (
                    <span className="dashboard__session-tag" title="Past-week review">
                      review
                    </span>
                  )}
                  <span className="dashboard__session-week">
                    {s.weekLabel ?? `Week ${s.weekId}`}
                  </span>
                </div>
                <div className="dashboard__session-score">
                  <span className="dashboard__session-score-num">
                    {s.firstPassScore ?? "—"}
                  </span>
                  <span className="dashboard__session-score-of">
                    /{s.totalWords}
                  </span>
                </div>
                {s.missedWords.length > 0 && (
                  <div className="dashboard__session-misses">
                    Missed: {s.missedWords.join(", ")}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ─── Stat tile ───────────────────────────────────────────────────────

function Stat({
  label,
  value,
  suffix,
  sub,
}: {
  label: string;
  value: number | string;
  suffix?: string;
  sub?: string;
}) {
  return (
    <div className="dashboard__stat">
      <div className="dashboard__stat-value">
        {value}
        {suffix && <span className="dashboard__stat-suffix"> {suffix}</span>}
      </div>
      <div className="dashboard__stat-label">{label}</div>
      {sub && <div className="dashboard__stat-sub">{sub}</div>}
    </div>
  );
}

// ─── Formatters ──────────────────────────────────────────────────────

/** "7.4" — one decimal place; backend hands a real average. */
function formatScore(n: number): string {
  return n.toFixed(1);
}

/** "2m 35s" / "45s" — Eva's sessions are short. */
function formatDuration(seconds: number): string {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

/** "Mon 18 May, 14:25" — locale-friendly without dragging in a date lib. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
  const day = d.getDate();
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${dow} ${day} ${month}, ${hh}:${mm}`;
}
